import React, { ReactElement, useEffect, useMemo, useRef, useCallback } from "react";
import { useSelector, useDispatch } from "react-redux";
import { Portal } from "../Portal";
import { ScreenLockAppState } from "./ScreenLock.types";
import { setScreenLocked } from "src/ui";
import { localize } from "src/l10n";
import { LargeBody, CaptionBody, SmallBodyBolder } from "../Text";
import { useWindow } from "@fluentui/react";

const pinArray = Array.from({ length: 4 }, (_, i) => i);

function ScreenLock(): ReactElement | null { 
    const wnd = useWindow();
    const dispatch = useDispatch();
    const inputRef = useRef<HTMLInputElement>();
    const [pinCode, setPinCode] = React.useState<string>("");

    const l10n = useMemo(() => ({
        sessionLocked: localize("SESSION_LOCKED"),
        sessionLockInfo: localize("SESSION_LOCK_INFO"),
        verifyAndContinue: localize("VERIFY_AND_CONTINUE"),
        cancelAndSignOut: localize("CANCEL_AND_SIGN_OUT"),
    }), []);

    const {
        isIdle,
        isLocked,
        screenLockEnabled,
    } = useSelector<Spintr.AppState, ScreenLockAppState>(
        (appState) => ({
            isIdle: appState.ui.userIsIdle,
            isLocked: appState.ui.screenLocked,
            screenLockEnabled: appState.profile.active.roles.includes("functionAccount"),
        })
    );

    const onPinChanged = useCallback(
        (event: React.ChangeEvent<HTMLInputElement>) => {
            const newPin = event.target.value.replace(/\D/g, ""); // Allow only digits

            setPinCode(newPin.substr(0, 4));
        }, [setPinCode]);

    const onPinBoxClicked = useCallback(
        () => {
            const input = inputRef.current;
            if (!input) {
                return;
            }

            input.focus();
            input.setSelectionRange(input.value.length, input.value.length);
        }, [inputRef]);

    const verify = useCallback(
        () => {
            // Logic to verify pin code
            if (pinCode === "1244") { // Example pin code check
                dispatch(setScreenLocked(false));
                setPinCode("");
            } else {
                alert("Incorrect PIN");
            }
        }, [pinCode, dispatch]);

    const onFormSubmitting = useCallback(
        (event: React.FormEvent<HTMLFormElement>) => {
            event.preventDefault();
            verify();
        }, [verify]);

    const onCancelClicked = useCallback(
        (event: React.MouseEvent) => {
            event.preventDefault();

            const navigator = wnd.navigator;
            if (navigator.userAgent.indexOf("MSIE") !== -1 || navigator.userAgent.indexOf("Trident") !== -1) {
                wnd.document.execCommand("ClearAuthenticationCache");
            }

            wnd.localStorage.clear();

            wnd.location.assign("/api/authentication/signout");
        }, [wnd]);

    useEffect(function _ScreenLockUpdate() {
        if (!isIdle || !screenLockEnabled) {
            return;
        }

        dispatch(setScreenLocked(true));
    }, [isIdle, screenLockEnabled]);

    const renderPinBox = useCallback(
        (index: number) => (
            <div
                key={index}
                className={`ScreenLock-pinBox ${pinCode[index] ? "filled" : ""}`}
                onClick={onPinBoxClicked}
            >
                {pinCode[index] || ""}
            </div>
        ), [pinCode, onPinBoxClicked]);

    if (!isLocked || !screenLockEnabled) {
        return null;
    }

    return (
        <Portal>
            <div className="ScreenLock" onClick={onPinBoxClicked}>
                <form className="ScreenLock-content"
                      onSubmit={onFormSubmitting}>
                    <div className="ScreenLock-text">
                        <LargeBody className="ScreenLock-title">
                            {l10n.sessionLocked}
                        </LargeBody>

                        <CaptionBody className="ScreenLock-info">
                            {l10n.sessionLockInfo}
                        </CaptionBody>
                    </div>

                    <div className="ScreenLock-pinInput">
                        <input autoFocus= {true}
                               className="ScreenLock-pinInputField"
                               maxLength={4}
                               onChange={onPinChanged}
                               placeholder="Enter PIN"
                               ref={inputRef}
                               type="password"
                               value={pinCode} />
                        <div className="ScreenLock-pinBoxes">
                            {pinArray.map(renderPinBox)}
                        </div>
                    </div>

                    <div className="ScreenLock-actions">
                        <button className="ScreenLock-verifyButton lightPrimaryBGColorHover"
                                role="button">
                            <SmallBodyBolder color="contentNormal">
                                {l10n.verifyAndContinue}
                            </SmallBodyBolder>
                        </button>

                        <button className="ScreenLock-signOutButton"
                                onClick={onCancelClicked}
                                role="button">
                            <SmallBodyBolder color="blue">
                                {l10n.cancelAndSignOut}
                            </SmallBodyBolder>
                        </button>
                    </div>
                </form>
            </div>
        </Portal>
    );
}

export default ScreenLock;

